import { useParams, useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useStudentDetailQuery } from './useStudentDetailQuery';
import StudentGeneralTab from './StudentGeneralTab';
import StudentEnrollmentTab from './StudentEnrollmentTab';
import { StudentGradeReport } from '~/components/StudentAcademics/StudentGradeReport';
import { StudentAcademicSummaryPanel } from '~/components/StudentAcademics/StudentAcademicSummaryPanel';
import type { AppLayoutContext } from '~/types';
import { useWayBack } from '~/hooks/useWayBack';

function StudentDetail() {
  const { personId } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL
  const isEnrollmentTab = location.pathname.endsWith('/enrollment');
  const isGradesTab = location.pathname.endsWith('/grades');
  const activeTab = isEnrollmentTab ? 'enrollment' : isGradesTab ? 'grades' : 'general';

  const selectedYear = roleContext?.selectedSchoolYear;

  const { data, loading, error, refetch } = useStudentDetailQuery(
    school.id,
    personId,
    selectedYear?.id,
  );

  const person = data?.school?.person;
  const enrollment = person?.studentEnrollmentForYear;
  const familyId = enrollment?.familyEnrollment?.family?.id;
  const gradeLevels = data?.school?.gradeLevelsSetting?.items || [];

  const basePath = `/app/${school.slug}/students/${personId}`;

  // Back past this student's tabs to wherever the person came from (a class
  // roster, Enrollments, a family). Opened directly, it goes to the family:
  // its enrollment tab from the enrollment tab, otherwise its general tab.
  const familySlug = enrollment?.familyEnrollment?.family?.slug;
  const familyName = enrollment?.familyEnrollment?.family?.name;
  const familyUrl = familySlug
    ? `/app/${school.slug}/families/${familySlug}${isEnrollmentTab ? '/enrollment' : ''}`
    : `/app/${school.slug}/families`;
  const { goBack, toPreviousScreen } = useWayBack(basePath, familyUrl);
  const backLabel = toPreviousScreen
    ? 'Back'
    : familySlug ? `Back to ${familyName}` : 'Back to Families';
  const backButton = (
    <Button variant="ghost" className="-ml-4 mb-4" onClick={goBack}>
      <ArrowLeft className="mr-2 h-4 w-4" />
      {backLabel}
    </Button>
  );

  const handleTabChange = (value: string) => {
    if (value === 'enrollment') {
      navigate(`${basePath}/enrollment`);
    } else if (value === 'grades') {
      navigate(`${basePath}/grades`);
    } else {
      navigate(basePath);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted rounded mb-6"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        {backButton}
        <div className="text-destructive">
          Error loading student: {error.message}
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="p-6">
        {backButton}
        <div className="text-muted-foreground">
          Student not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        {backButton}
        <h1 className="text-2xl font-semibold tracking-tight">
          {person.fullName}
        </h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="enrollment">
            Enrollment {selectedYear ? `(${selectedYear.name})` : ''}
          </TabsTrigger>
          <TabsTrigger value="grades">Grades</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-6">
          <StudentGeneralTab
            person={person}
            schoolSlug={school.slug}
            onRefetch={refetch}
          />
        </TabsContent>
        <TabsContent value="enrollment" className="mt-6">
          <StudentEnrollmentTab
            person={person}
            enrollment={enrollment}
            selectedYear={selectedYear}
            schoolSlug={school.slug}
            familyId={familyId}
            gradeLevels={gradeLevels}
            checklistEnrollmentOn={data?.school?.checklistEnrollmentOn ?? false}
            onRefetch={refetch}
          />
        </TabsContent>
        <TabsContent value="grades" className="mt-6 space-y-4">
          {selectedYear ? (
            <>
              <StudentGradeReport
                schoolId={school.id}
                schoolYearId={selectedYear.id}
                personId={person.id}
                schoolName={school.name}
                schoolYearName={selectedYear.name}
                studentName={person.fullName}
              />
              <StudentAcademicSummaryPanel
                schoolId={school.id}
                schoolYearId={selectedYear.id}
                personId={person.id}
                title={`This year (${selectedYear.name})`}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Select a school year to see grades.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default StudentDetail;
